import { dataCenterFinancialModelV1Schema } from './dataCenterFinancial';
/**
 * Financial Assumptions (FA pack — FA1).
 *
 * Schema for the user-provided project-economics inputs that drive
 * LCOE / IRR / NPV / payback in feasibility, power-availability, and
 * interconnection reports.
 *
 * Architectural pattern (mirrors SC2 project-intent / site-context):
 *   - Canonical state lives on `SiteEntity.financialAssumptions`.
 *   - Per-report opt-in override on `SiteRagReport.financialAssumptionsOverride`.
 *   - Gen-time snapshot on `SiteRagReport.financialAssumptionsAtGen`
 *     pins the report to the values it was generated against.
 *
 * Resolution waterfall (highest priority first):
 *   1. report.financialAssumptionsOverride
 *   2. site.financialAssumptions
 *   3. null (UI renders empty state)
 *
 * Migration discipline: no backfill. Sites without financial
 * assumptions render as "Not set yet" and contribute nothing to the
 * LLM prompt.
 *
 * Units note (the codebase's #1 regression source): every monetary
 * field is plain USD (not USD-thousand / USD-million). Per-energy
 * fields are $/MWh (not $/kWh). Per-kW fields are $/kW (not $/MW).
 * Project-finance formulas in `src/lib/projectFinance.ts` rely on
 * these units being consistent end-to-end.
 */

/**
 * Where a single field's current value originated. Used by the
 * Financial Assumptions form to render provenance chips and by the
 * extractor to surface confidence to the post-upload review modal.
 *
 * The compact `FieldProvenanceSource` enum is intentionally narrow so
 * the review modal can group fields into three confidence tiers:
 *   - `extracted-named-range` ⇒ tier-1, high confidence
 *   - `extracted-label-match` ⇒ tier-2, medium confidence
 *   - `extracted-llm`         ⇒ tier-3, low confidence
 *   - `manual` / `industry-baseline` / `derived` ⇒ user-or-system
 *                                                  authored (no
 *                                                  review needed)
 */
export type FieldProvenanceSource =
  | 'extracted-named-range'
  | 'extracted-label-match'
  | 'extracted-llm'
  // PDF-extracted. Introduced for FA13 PPA term-sheets; `extractPpaTerms.ts`
  // was removed as dormant (restore from 9cb2c600c). The provenance values stay —
  // they describe HOW a field was filled, and `extractQuoteFromPdf.ts` still
  // fills fields this way.
  | 'extracted-defined-pattern'
  | 'extracted-section'
  // Context Binding Engine (CBE) — auto-filled from a tagged ExtractedClaim
  // in `site.siteContext.claims[]` (the "Pull from BOM + Intent" third
  // source). The `cellReference` carries the originating claim tag + a
  // verbatim source-span excerpt so the review modal can render a "from
  // {context}" chip and the user can verify the fill against its source.
  | 'extracted-context'
  | 'manual'
  | 'industry-baseline'
  | 'derived';

export type FieldProvenanceConfidence = 'high' | 'medium' | 'low';

export interface FieldProvenance {
  source: FieldProvenanceSource;
  confidence: FieldProvenanceConfidence;
  /**
   * For `extracted-*` sources: the spreadsheet cell or PDF span the
   * value was lifted from. e.g. `'Inputs!B14'` (xlsx) or
   * `'p.5 §V.1.a'` (PDF, used by FA13).
   */
  cellReference?: string;
  /**
   * For `industry-baseline` source: the citation slug for the source
   * dataset. e.g. `'NREL ATB 2024 SMR NOAK'`. The form renders this in
   * the provenance chip.
   */
  baselineRef?: string;
  /**
   * ISO timestamp when this value was last written. Lets the form
   * sort "freshly edited" fields and lets the review modal show
   * "extracted 2 min ago".
   */
  extractedAt: string;
}

export type FinancialTechnology =
  | 'smr'
  | 'gas-cc'
  | 'gas-sc'
  | 'solar-bess'
  | 'wind-bess'
  | 'mixed'
  | 'tbd';

export type DepreciationMethod =
  | 'macrs-5'
  | 'macrs-7'
  | 'macrs-15'
  | 'straight-line';

export type FinancialAssumptionsSource =
  | 'uploaded-model'
  | 'manual-entry'
  | 'industry-baseline'
  | 'hybrid';

/**
 * Currency code. ISO-4217 strings — typed as a union of the common
 * codes plus `string` so callers can pass any ISO code without a
 * compiler complaint. `exchangeRateToUsd` carries the conversion
 * factor; project-finance formulas always operate in USD after the
 * boundary normalisation in `recompute()`.
 */
export type CurrencyCode = 'USD' | 'BWP' | 'EUR' | 'GBP' | string;

/**
 * How a scenario adjustment transforms the field's current value `x`:
 *   multiplier — `x * value`   (0.9 ⇒ −10 %)
 *   delta      — `x + value`   (+5 on a `*Pct` field ⇒ +5 percentage points)
 *   absolute   — `value`       (pin the field)
 */
export type ScenarioAdjustmentKind = 'multiplier' | 'delta' | 'absolute';

export interface ScenarioAdjustment {
  kind: ScenarioAdjustmentKind;
  value: number;
}

/**
 * The numeric-valued keys of `FinancialAssumptionsCore` — the only fields an
 * adjustment can target. `technology`, `inServiceDate`, `depreciationMethod`
 * and the nested `landCost` object are excluded by construction.
 */
export type ScenarioAdjustableField = {
  [K in keyof FinancialAssumptionsCore]-?:
    FinancialAssumptionsCore[K] extends number | null | undefined ? K : never;
}[keyof FinancialAssumptionsCore];

export type ScenarioAdjustments = Partial<Record<ScenarioAdjustableField, ScenarioAdjustment>>;

export interface FinancialScenario {
  id: string;
  label: string;
  /**
   * Diff against the base case — only the fields that differ between
   * scenarios are stored. The driver merges the partial onto the
   * canonical assumptions at recompute time. Excludes the metadata
   * fields (`source` / `currency` / `scenarios` / etc.) so a scenario
   * diff is purely the project-economics delta.
   */
  assumptions: Partial<FinancialAssumptionsCore>;
  /**
   * RELATIVE adjustments, applied AFTER `assumptions` and re-derived against
   * the CURRENT base every time the scenario is merged.
   *
   * Why this exists: the seeded Upside/Downside cases used to freeze the
   * computed NUMBER at seed time (`base.capexTotalUsd * 0.9`). When the base
   * later moved, "Upside (−10% CAPEX)" silently became −55% — and if it was the
   * canonical scenario, the whole verdict ran on it. An adjustment says what it
   * means and stays true.
   *
   * `assumptions` remains the channel for a hand-authored ABSOLUTE override
   * ("CAPEX is exactly $2B"), which must not be re-derived.
   */
  adjustments?: ScenarioAdjustments;
  isCanonical: boolean;
}

/**
 * Project-economics core — the fields that flow through the project
 * finance formulas. Excludes metadata + scenarios so a `Partial<Core>`
 * can be a clean scenario diff.
 */
export interface FinancialAssumptionsCore {
  // Project basics
  technology: FinancialTechnology;
  capacityMW: number | null;
  capacityFactorPct: number | null;
  projectLifeYears: number | null;
  /** ISO date; quarter precision OK (e.g. '2030-01-01' for 2030-Q1). */
  inServiceDate: string | null;

  // Capital
  capexTotalUsd: number | null;
  /**
   * Derived when `capexTotalUsd` and `capacityMW` are both present
   * (capexTotalUsd / (capacityMW * 1000)). User-edits override the
   * derivation; the form flips the provenance chip to `manual`.
   */
  capexPerKwUsd: number | null;
  constructionMonths: number | null;
  decommissioningUsd: number | null;
  workingCapitalUsd: number | null;
  contingencyPct: number | null;

  // Operating
  fixedOmPerKwYearUsd: number | null;
  variableOmPerMwhUsd: number | null;
  /** gas / SMR only — null for solar / wind / storage. */
  fuelCostPerMwhUsd: number | null;
  insuranceAndPropertyTaxPct: number | null;

  // Financing
  /** Debt fraction in [0, 1]. e.g. 0.7 ⇒ 70:30 debt-to-equity. */
  debtToEquityRatio: number | null;
  debtRatePct: number | null;
  equityHurdlePct: number | null;
  /**
   * Derived when `debtToEquityRatio` + `debtRatePct` + `equityHurdlePct`
   * + `taxRatePct` are all present:
   *   WACC = D × r_d × (1 - t) + (1 - D) × r_e
   * User edits override the derivation.
   */
  waccPct: number | null;
  taxRatePct: number | null;
  /** Investment Tax Credit, percentage of CAPEX. */
  itcPct: number | null;
  /** Production Tax Credit, $/MWh (year-1 nominal). */
  ptcPerMwhUsd: number | null;
  depreciationMethod: DepreciationMethod | null;

  // PPA / revenue
  ppaPricePerMwhUsd: number | null;
  ppaEscalatorPctPerYear: number | null;
  ppaTermYears: number | null;

  // Output metrics — extracted from a model or recomputed from inputs
  lcoeNominalPerMwhUsd: number | null;
  lcoeRealPerMwhUsd: number | null;
  npvUsd: number | null;
  projectIrrPct: number | null;
  equityIrrPct: number | null;
  paybackYears: number | null;

  /**
   * Multi-Parcel Site Boundary (MP pack, MP7) — land-cost component
   * of CAPEX with optional per-parcel breakdown. Helpful when parcels
   * have different per-acre prices (e.g. one parcel is a long-leased
   * substation easement and another is fee-simple datacenter land).
   *
   * - `totalUsd` is the rolled-up land-cost basis (single number for
   *   single-parcel sites; sum across parcels for multi-parcel sites).
   * - `perParcel`, when set, MUST reconcile to `totalUsd` within
   *   rounding tolerance — the FinancialAssumptionsForm
   *   CrossValidationStrip surfaces mismatches.
   *
   * Optional everywhere — legacy assumptions records leave it
   * undefined and the project-finance formulas treat undefined as
   * "land cost rolled into capexTotalUsd" (existing behaviour).
   */
  landCost?: {
    totalUsd: number | null;
    perParcel?: Array<{
      /** `SiteBoundaryParcel.id` — stable across SED rebuilds. */
      parcelId: string;
      usdPerAcre: number;
      totalUsd: number;
    }>;
  };
}

/** R-58 — per-phase economics (native phased financial model). */
export type FinancialPhaseStatus = 'planned' | 'in-progress' | 'in-service' | 'cancelled';

export interface FinancialPhase {
  /** Stable id — aligns with `projectIntent.phases[].id` when linked. */
  phaseId: string;
  /** Human label, e.g. "Phase 1" or "SMR Phase 3". */
  label?: string;
  /** 1-based sort order for tabs and ordering warnings. */
  sequence: number;
  capacityMW: number | null;
  capexTotalUsd: number | null;
  /** Derived when capex + capacity set; user edits override. */
  capexPerKwUsd: number | null;
  capacityFactorPct: number | null;
  /** ISO date; quarter precision OK. */
  inServiceDate: string | null;
  constructionMonths: number | null;
  fixedOmPerKwYearUsd?: number | null;
  variableOmPerMwhUsd?: number | null;
  fuelCostPerMwhUsd?: number | null;
  technology?: FinancialTechnology;
  status?: FinancialPhaseStatus;
}

/**
 * Schema generation.
 *   1 — legacy flat (campus fields only, no `phases[]`).
 *   2 — native `phases[]`, but written by the campus-first writers: a phase
 *       could be left empty while the core carried values, so a wholly-empty
 *       phase is HEALED from the core on read.
 *   3 — phase-authoritative: written through `writeFinancialAssumptions` /
 *       `applyCampusPatch`, so `phases[]` is taken LITERALLY — a null in a
 *       phase is a value the user cleared and is never re-filled from the core.
 *
 * The generation is what makes "legacy" a fact rather than an inference: it
 * does not depend on any caller remembering to re-roll the campus fields.
 */
export type FinancialAssumptionsModelVersion = 1 | 2 | 3;

/** The generation written by the current phase-authoritative write path. */
export const FINANCIAL_MODEL_VERSION_CURRENT = 3 as const;

/**
 * Canonical FinancialAssumptions surface — the project-economics core
 * + metadata + scenarios + per-field provenance.
 */
export interface FinancialAssumptions extends FinancialAssumptionsCore {
  /** Independent of phase modelVersion; absence preserves generation/PPA. */
  businessModelKind?: 'generation-ppa' | 'data-center-capacity-fee';
  dataCenterModel?: import('./dataCenterFinancial').DataCenterFinancialModelV1;
  /** Owning site — stamped on read/write (R-58). */
  siteId?: string;
  /**
   * Per-phase breakdowns (R-58) — AUTHORITATIVE from generation 2 onward.
   *
   * The phased engine reads each phase's `capacityFactorPct`, `inServiceDate`
   * and O&M / fuel rates (`projectFinancePhased.PhaseCapexSource`, populated by
   * `buildFinancialAnalysis`), falling back to the campus value FIELD BY FIELD.
   * Campus rollup fields on the core are derived from this array via
   * `syncCampusRollupFromPhases` — they are outputs, not inputs, which is why a
   * campus-level write must go through `applyCampusPatch`.
   *
   * (Before 2026-08-19 this comment claimed a preference the engine did not
   * actually honour: every phase was priced at the campus capacity factor and
   * given a capacity-fraction slice of campus OPEX, so a per-phase edit in the
   * form changed no number in any report.)
   */
  phases?: FinancialPhase[];
  /** R-58 schema generation — absent treated as 1 (legacy flat). */
  modelVersion?: FinancialAssumptionsModelVersion;
  /**
   * SCM procurement commercial terms snapshot — derived from BoM/awarded
   * quote data during explicit Supply Chain sync so finance reviewers can
   * see deposit burden and payment/validity posture alongside CAPEX.
   */
  procurementCommercials?: {
    depositTotalUsd: number;
    depositShareOfCapex: number | null;
    itemsWithDepositCount: number;
    averageValidityDays: number | null;
    paymentTermsSummary: string[];
    freightTermsSummary: string[];
    /** `supplyChainPlan.updatedAt` this snapshot was taken from. */
    updatedAt: string;
    /**
     * Set when the live Bill of Materials has moved on since this snapshot was
     * taken (`plan.updatedAt` here). The figures are kept — a superseded number
     * a reader can date is more useful than a blank — but every surface MUST
     * label them as historical rather than present them as current.
     *
     * Before this existed the snapshot was carried forward by every `{...fa}`
     * spread with no expiry at all, so the report's deposit sentence could
     * describe a BoM that no longer existed.
     */
    supersededAt?: string;
  };

  /** Optional named scenarios; one is marked canonical (LLM-read). */
  scenarios?: FinancialScenario[];

  // Metadata
  source: FinancialAssumptionsSource;
  /** SiteDocument id when `source === 'uploaded-model'` (or hybrid). */
  sourceFileId?: string;
  sourceFileName?: string;
  /**
   * Pins reports to a specific model version. Reports stamped with
   * this hash can be regenerated against the same uploaded model
   * months later for a clean diff.
   */
  sourceFileHash?: string;
  currency: CurrencyCode;
  /** Required when `currency !== 'USD'`. Project-finance formulas
   *  normalise via this at the boundary. */
  exchangeRateToUsd?: number;
  updatedAt: string;
  /** Free-form — typically uid or email. */
  updatedBy: string;

  /**
   * Per-field provenance map. Keys are field names of
   * `FinancialAssumptionsCore`, plus the nested `'landCost.totalUsd'`
   * dot-path (the one nested core field the CBE context fill writes —
   * `landCost` itself is an object, so its provenance is per-leaf).
   * Missing entries mean the field has never been written (renders as
   * the empty state).
   */
  fieldProvenance: Partial<Record<keyof FinancialAssumptionsCore | 'landCost.totalUsd', FieldProvenance>>;

  /**
   * Per-PHASE field provenance map (Financial auto-populate / R-58).
   * Keys are `${phaseId}:${field}` (field ∈ capacityMW | capexTotalUsd |
   * capexPerKwUsd | inServiceDate). Written by the `pullFinancialFromSources`
   * action so a subsequent auto-pull can tell a `'derived'` cell (safe to
   * refresh from a fresh BOM rollup / intent sync) from a user `'manual'`
   * cell (protected — never clobbered). Absent ⇒ no auto-pull has run yet,
   * so every current phase value is treated as user-authored.
   */
  phaseFieldProvenance?: Record<string, FieldProvenance>;
}

/**
 * Convenience type — the keys of `FinancialAssumptionsCore` (i.e. the
 * fields that carry per-field provenance). Used by the form, the
 * extractor, the review modal, and `buildFinancialAssumptionsBlock`.
 */
export type FinancialField = keyof FinancialAssumptionsCore;

/**
 * Forward-compat alias used by the FA13-16 surfaces (PPA parsing, IRA
 * credits, precedent comparison, cross-validation). Those readers only
 * consume a handful of numeric fields and accept sparse inputs (test
 * fixtures + on-the-fly partial records), so they import this Partial
 * alias instead of the full record.
 */
export type FinancialAssumptionsLike = Partial<FinancialAssumptionsCore>;

/**
 * Canonical empty state. Use this to seed a fresh
 * FinancialAssumptions for manual entry or industry-baseline paths.
 *
 * Note: `technology` defaults to `'tbd'`, NOT `null`, because the rest
 * of the platform's UI (helper hints, outlier ranges, IRA tax-credit
 * eligibility) is technology-conditioned. `'tbd'` makes the
 * "tell us your tech first" prompt explicit instead of crashing the
 * conditional renders.
 */
const SINGLE_PHASE_ID = 'single-phase';

/** Derive $/kW from total capex and capacity when both are finite. */
export function deriveCapexPerKwUsd(
  capexTotalUsd: number | null,
  capacityMW: number | null,
): number | null {
  if (
    capexTotalUsd == null
    || capacityMW == null
    || !Number.isFinite(capexTotalUsd)
    || !Number.isFinite(capacityMW)
    || capacityMW <= 0
  ) {
    return null;
  }
  return capexTotalUsd / (capacityMW * 1000);
}

/** Build one `FinancialPhase` from legacy campus-level core fields. */
export function financialPhaseFromCore(
  core: FinancialAssumptionsCore,
  phaseId: string = SINGLE_PHASE_ID,
  sequence: number = 1,
  label?: string,
): FinancialPhase {
  return {
    phaseId,
    label: label ?? 'Phase 1',
    sequence,
    capacityMW: core.capacityMW,
    capexTotalUsd: core.capexTotalUsd,
    capexPerKwUsd: core.capexPerKwUsd ?? deriveCapexPerKwUsd(core.capexTotalUsd, core.capacityMW),
    capacityFactorPct: core.capacityFactorPct,
    inServiceDate: core.inServiceDate,
    constructionMonths: core.constructionMonths,
    fixedOmPerKwYearUsd: core.fixedOmPerKwYearUsd,
    variableOmPerMwhUsd: core.variableOmPerMwhUsd,
    fuelCostPerMwhUsd: core.fuelCostPerMwhUsd,
    technology: core.technology,
    status: 'planned',
  };
}

/**
 * Campus TOTAL capacity under the cumulative convention = the FINAL
 * (max-sequence) cumulative phase capacity. Defensively Math.max over finite
 * >0 capacities (== last value when monotonic, the form rule). Null when no
 * phase carries a capacity. Mirrors `effectivePhases().totalCapacityMW` on
 * the intent side so financial + intent campus totals agree (no spurious
 * cross-validate warning).
 */
export function finalPhaseCapacityMw(phases: FinancialPhase[]): number | null {
  const values = phases
    .map(p => p.capacityMW)
    .filter((mw): mw is number => mw != null && Number.isFinite(mw) && mw > 0);
  if (values.length === 0) return null;
  return Math.max(...values);
}

/**
 * Per-phase capacity INCREMENT (MW ADDED that phase) under the cumulative
 * convention, for `FinancialPhase[]`. Sorts by `sequence`, then
 * increment_i = cap_i − cap_{i−1} (i==0 ⇒ cap_0), floored at 0. Mirrors
 * `phaseIncrementMW` (projectIntent.ts) for the financial phase shape. Used
 * by CF-weighting + per-phase $/kW so the energy/cost basis is the new
 * plant, not the double-counting cumulative figure.
 *
 * @param phases  Financial phases (any order — sorted by sequence here).
 * @param i       Index INTO THE SORTED array.
 */
export function financialPhaseIncrementMw(phases: FinancialPhase[], i: number): number | null {
  const sorted = [...phases].sort((a, b) => a.sequence - b.sequence);
  const cur = sorted[i]?.capacityMW;
  if (cur == null || !Number.isFinite(cur)) return null;
  if (i === 0) return Math.max(0, cur);
  const prevRaw = sorted[i - 1]?.capacityMW;
  const prev = prevRaw != null && Number.isFinite(prevRaw) ? prevRaw : 0;
  return Math.max(0, cur - prev);
}

/** Roll campus totals from phases into core fields (does not mutate input). */
export function rollupCampusFromPhases(
  phases: FinancialPhase[],
  siteWide: FinancialAssumptionsCore,
): Partial<FinancialAssumptionsCore> {
  // ── NO SILENT RESURRECTION (fixed 2026-08-19) ───────────────────────────
  // When `phases[]` exists it is AUTHORITATIVE: the rollup must report what the
  // phases say, including when they say "nothing". Falling back to the campus
  // value for a phase field the user just CLEARED made the clear impossible —
  // the form showed blank, the record kept the old number, and
  // `syncSinglePhaseFromCampus` then wrote the stale value back into the phase
  // on the next read. The `siteWide` fallbacks below therefore apply ONLY when
  // there are no phases to speak for the campus.
  const hasPhases = phases.length > 0;
  // CUMULATIVE convention: campus capacity = FINAL/MAX cumulative, NOT the
  // sum of phase capacities (summing a 50/100/200/300 build would give 650
  // instead of the true ultimate 300).
  const totalMw = finalPhaseCapacityMw(phases);
  // Per-phase CAPEX is the INCREMENTAL cost of each phase, so summing it is
  // correct (FinancialPhase.capexTotalUsd is sourced from the per-phase BoM
  // increment — see syncCapexFromSupplyChain.computePerPhaseCapexFromBom).
  const pricedPhaseCapex = phases
    .map(p => p.capexTotalUsd)
    .filter((v): v is number => v != null && Number.isFinite(v));
  const totalCapex = pricedPhaseCapex.reduce((a, b) => a + b, 0);
  const capexTotalUsd = pricedPhaseCapex.length > 0
    ? totalCapex
    : (hasPhases ? null : siteWide.capexTotalUsd);
  const capacityMW = totalMw ?? (hasPhases ? null : siteWide.capacityMW);
  const sorted = [...phases].sort((a, b) => a.sequence - b.sequence);
  const phaseCods = [...sorted]
    .map(p => p.inServiceDate)
    .filter((d): d is string => Boolean(d))
    .sort();
  const lastCod = phaseCods.at(-1) ?? (hasPhases ? null : siteWide.inServiceDate);
  // CF blend weights each phase by the ENERGY contributed by its NEW plant
  // = the per-phase INCREMENT, not the cumulative figure (which would
  // double-count earlier phases' MW into every later phase).
  const campusFallbackCf = hasPhases ? null : siteWide.capacityFactorPct;
  const cfWeighted = (() => {
    if (totalMw == null || totalMw <= 0) return campusFallbackCf;
    let sum = 0;
    let weight = 0;
    sorted.forEach((p, i) => {
      const incr = financialPhaseIncrementMw(sorted, i);
      if (incr == null || incr <= 0 || p.capacityFactorPct == null) return;
      sum += incr * p.capacityFactorPct;
      weight += incr;
    });
    return weight > 0 ? sum / weight : campusFallbackCf;
  })();
  return {
    capacityMW,
    capexTotalUsd: capexTotalUsd ?? null,
    capexPerKwUsd: deriveCapexPerKwUsd(capexTotalUsd ?? null, capacityMW),
    capacityFactorPct: cfWeighted,
    inServiceDate: lastCod ?? null,
  };
}

/**
 * Wrap legacy flat assumptions into model v2 with a one-element `phases[]`.
 * Idempotent when already v2 with non-empty phases.
 */
/**
 * True when a phase carries NO quantities at all — the shape a synthetic
 * placeholder has before anything is entered. Distinguishes a legacy record
 * that needs seeding from the campus core (nothing to lose) from one where the
 * user has deliberately cleared a field (must be preserved).
 */
export function isPhaseWhollyEmpty(phase: FinancialPhase): boolean {
  return phase.capacityMW == null
    && phase.capexTotalUsd == null
    && phase.capexPerKwUsd == null
    && phase.capacityFactorPct == null
    && phase.inServiceDate == null
    && phase.constructionMonths == null;
}

/**
 * SEEDING HELPER ONLY — fill a lone phase's null fields from the campus core.
 *
 * Use this when CREATING a v2 record's first phase out of legacy flat fields.
 * Do NOT use it on an established v2 record: there, `phases[]` is authoritative,
 * and copying campus values back down resurrects figures the user just cleared
 * (the campus fields are themselves derived FROM the phases, so the "source"
 * is the previous rollup of the very value being overwritten).
 *
 * Campus-level writes on an established record go through `applyCampusPatch`.
 */
export function syncSinglePhaseFromCampus(fa: FinancialAssumptions): FinancialAssumptions {
  const phases = fa.phases ?? [];
  if (phases.length !== 1) return fa;
  const p = phases[0];
  const fromCore = financialPhaseFromCore(fa, p.phaseId, p.sequence, p.label);
  const merged: FinancialPhase = {
    ...fromCore,
    ...p,
    capacityMW: p.capacityMW ?? fromCore.capacityMW,
    capexTotalUsd: p.capexTotalUsd ?? fromCore.capexTotalUsd,
    capexPerKwUsd: p.capexPerKwUsd ?? fromCore.capexPerKwUsd,
    capacityFactorPct: p.capacityFactorPct ?? fromCore.capacityFactorPct,
    inServiceDate: p.inServiceDate ?? fromCore.inServiceDate,
    constructionMonths: p.constructionMonths ?? fromCore.constructionMonths,
    technology: p.technology ?? fromCore.technology,
  };
  return { ...fa, phases: [merged] };
}

export function migrateFinancialAssumptionsRecord(
  raw: FinancialAssumptions,
): FinancialAssumptions {
  if (raw.modelVersion === FINANCIAL_MODEL_VERSION_CURRENT && (raw.phases?.length ?? 0) > 0) {
    // PHASE-AUTHORITATIVE — taken literally. A null is a cleared value.
    return { ...raw, phases: sortFinancialPhases(raw.phases!) };
  }
  if (raw.modelVersion === 2 && (raw.phases?.length ?? 0) > 0) {
    // LEGACY v2 — written before the write path became phase-authoritative.
    //
    // This used to run `syncSinglePhaseFromCampus` unconditionally, back-filling
    // EVERY null phase field from the core. Combined with the campus rollup on
    // the way out, that made clearing a phase value impossible: the form showed
    // blank, the rollup kept the stale campus number, and the back-fill wrote it
    // straight back into the phase on the next read.
    //
    // The ONE case that still needs healing is a LEGACY record: a v2 document
    // whose lone phase is WHOLLY EMPTY while the core carries values — written
    // by the campus-only writers that predate `applyCampusPatch`. Reading those
    // literally would blank a model the user can see data in.
    //
    // Scope: ONLY a v2 document, and only a wholly-empty lone phase. Anything
    // saved since is v3 and is never healed, so an intentional clear sticks
    // without relying on the caller having re-rolled the campus fields first.
    const sorted = sortFinancialPhases(raw.phases!);
    if (sorted.length === 1 && isPhaseWhollyEmpty(sorted[0])) {
      return syncSinglePhaseFromCampus({ ...raw, phases: sorted });
    }
    return { ...raw, phases: sorted };
  }
  const phase = financialPhaseFromCore(raw);
  const phases = [phase];
  const rollup = rollupCampusFromPhases(phases, raw);
  return {
    ...raw,
    ...rollup,
    phases,
    modelVersion: FINANCIAL_MODEL_VERSION_CURRENT,
  };
}

export function sortFinancialPhases(phases: FinancialPhase[]): FinancialPhase[] {
  return [...phases].sort((a, b) => a.sequence - b.sequence);
}

/** Next-year ISO date from an optional base ISO date. */
export function nextYearIsoDate(baseIso: string | null | undefined): string {
  const year = baseIso && baseIso.length >= 4
    ? parseInt(baseIso.slice(0, 4), 10)
    : new Date().getFullYear();
  const next = Number.isFinite(year) ? year + 1 : new Date().getFullYear() + 1;
  const monthDay = baseIso && baseIso.length >= 7 ? baseIso.slice(4) : '-06-01';
  return `${next}${monthDay}`;
}

/**
 * Defaults for "Add Phase": +5% capex vs prior phase, commissioning next year.
 */
export function defaultNextFinancialPhase(
  prior: FinancialPhase | undefined,
  campusTechnology: FinancialTechnology,
): FinancialPhase {
  const sequence = (prior?.sequence ?? 0) + 1;
  const priorCapex = prior?.capexTotalUsd;
  const capexTotalUsd =
    priorCapex != null && Number.isFinite(priorCapex)
      ? priorCapex * 1.05
      : null;
  const capacityMW = prior?.capacityMW ?? null;
  return {
    phaseId: `phase-${sequence}-${Date.now()}`,
    label: `Phase ${sequence}`,
    sequence,
    capacityMW,
    capexTotalUsd,
    capexPerKwUsd: deriveCapexPerKwUsd(capexTotalUsd, capacityMW),
    capacityFactorPct: prior?.capacityFactorPct ?? null,
    inServiceDate: nextYearIsoDate(prior?.inServiceDate),
    constructionMonths: prior?.constructionMonths ?? null,
    fixedOmPerKwYearUsd: prior?.fixedOmPerKwYearUsd,
    variableOmPerMwhUsd: prior?.variableOmPerMwhUsd,
    fuelCostPerMwhUsd: prior?.fuelCostPerMwhUsd,
    technology: prior?.technology ?? campusTechnology,
    status: 'planned',
  };
}

/**
 * Campus-level fields that the phase rollup DERIVES. A write to any of these
 * must land on a phase — writing it on the core is silently discarded by
 * `syncCampusRollupFromPhases` on the next save.
 */
export const PHASE_DERIVED_CAMPUS_FIELDS = [
  'capacityMW',
  'capexTotalUsd',
  'capexPerKwUsd',
  'capacityFactorPct',
  'inServiceDate',
] as const;

export type PhaseDerivedCampusField = (typeof PHASE_DERIVED_CAMPUS_FIELDS)[number];

export interface ApplyCampusPatchResult {
  /** The patched record (already phase-synced; pass to `writeFinancialAssumptions`). */
  next: FinancialAssumptions;
  /**
   * Fields the patch could NOT apply because the model has multiple phases and
   * the campus value cannot be attributed to one of them. Callers MUST surface
   * these — silently dropping them is the bug this function exists to end.
   */
  unapplied: PhaseDerivedCampusField[];
}

/**
 * Apply a CAMPUS-LEVEL patch to a phase-authoritative financial model.
 *
 * WHY THIS EXISTS. `phases[]` is authoritative on write: every save re-derives
 * campus capacity / CAPEX / CF / COD from the phases. So a writer that patches
 * only the core — the uploaded-model commit, the "Sync to intent" capacity
 * button, the BoM CAPEX sync, the context fill — had its value overwritten by
 * the next save while its provenance chip still claimed the value was
 * `extracted`/`derived`. The user saw a stamped, sourced number that the record
 * did not contain.
 *
 * Behaviour:
 *   - Fields the rollup does NOT derive (financing, opex, revenue, metadata)
 *     are written straight onto the core.
 *   - Rollup-derived fields are written onto the SINGLE phase when there is
 *     exactly one — which is the shape every campus-level writer is built for.
 *   - With MULTIPLE phases a campus figure cannot be attributed to a phase, so
 *     it is NOT applied and is reported in `unapplied` for the caller to
 *     surface ("this model has 3 phases — edit the phase you mean").
 *
 * Pure; does not mutate the input.
 */
export function applyCampusPatch(
  fa: FinancialAssumptions,
  patch: Partial<FinancialAssumptionsCore>,
): ApplyCampusPatchResult {
  const derived = new Set<string>(PHASE_DERIVED_CAMPUS_FIELDS);
  const corePatch: Record<string, unknown> = {};
  const phasePatch: Record<string, unknown> = {};
  const unapplied: PhaseDerivedCampusField[] = [];

  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (derived.has(key)) phasePatch[key] = value;
    else corePatch[key] = value;
  }

  const phases = fa.phases ?? [];
  let nextPhases = phases;

  if (Object.keys(phasePatch).length > 0) {
    if (phases.length === 1) {
      nextPhases = [{ ...phases[0], ...phasePatch } as FinancialPhase];
    } else if (phases.length === 0) {
      // No phases yet — the core IS the model; the rollup has nothing to
      // overwrite it with, so a core write is safe.
      Object.assign(corePatch, phasePatch);
    } else {
      for (const key of Object.keys(phasePatch)) unapplied.push(key as PhaseDerivedCampusField);
    }
  }

  const next: FinancialAssumptions = {
    ...fa,
    ...(corePatch as Partial<FinancialAssumptions>),
    ...(nextPhases !== phases ? { phases: nextPhases } : {}),
  };
  return { next: nextPhases.length > 0 ? syncCampusRollupFromPhases(next) : next, unapplied };
}

/**
 * A campus-level value that the phase rollup is about to DISCARD.
 *
 * `phases[]` is authoritative on write, so `writeFinancialAssumptions` re-derives
 * the campus capacity / CAPEX / $/kW / capacity factor / COD from the phases. A
 * writer that sets one of those on the CORE therefore produces a number the save
 * silently drops — and, historically, stamped `extracted-*` / `derived`
 * provenance beside it, so the user saw a sourced figure the record did not hold.
 */
export interface DiscardedCampusWrite {
  field: PhaseDerivedCampusField;
  /** What the caller asked for. */
  attempted: number | string | null;
  /** What the phase rollup will store instead. */
  rolledUp: number | string | null;
}

/**
 * Detect campus-level writes that the phase rollup will discard.
 *
 * PURE — call it on the record BEFORE `writeFinancialAssumptions` normalises it.
 * Returns `[]` for a record with no phases (nothing to roll up from) and for one
 * whose campus fields already agree with its phases (the normal case: the form
 * re-rolls as the user types).
 *
 * This is the precise form of a guard that cannot be written as a text search:
 * the field names (`capacityMW`, `inServiceDate`, …) sit on a dozen unrelated
 * shapes, and writing them onto a PHASE is the correct, common case. Only the
 * VALUE COMPARISON distinguishes a discarded campus write from ordinary code.
 */
export function detectDiscardedCampusWrites(
  fa: FinancialAssumptions,
): DiscardedCampusWrite[] {
  const phases = fa.phases ?? [];
  if (phases.length === 0) return [];
  const rollup = rollupCampusFromPhases(phases, fa);
  const out: DiscardedCampusWrite[] = [];
  for (const field of PHASE_DERIVED_CAMPUS_FIELDS) {
    const attempted = fa[field] ?? null;
    const rolledUp = rollup[field] ?? null;
    if (attempted == null && rolledUp == null) continue;
    // Tolerate float noise on the derived $/kW.
    if (typeof attempted === 'number' && typeof rolledUp === 'number') {
      const scale = Math.max(Math.abs(attempted), Math.abs(rolledUp), 1);
      if (Math.abs(attempted - rolledUp) <= scale * 1e-9) continue;
    } else if (attempted === rolledUp) {
      continue;
    }
    out.push({ field, attempted, rolledUp });
  }
  return out;
}

export function syncCampusRollupFromPhases(fa: FinancialAssumptions): FinancialAssumptions {
  const phases = fa.phases ?? [];
  if (phases.length === 0) return fa;
  const rollup = rollupCampusFromPhases(phases, fa);
  return { ...fa, ...rollup, phases: sortFinancialPhases(phases) };
}

/**
 * Read path — legacy flat records become v2 with a one-element `phases[]`.
 */
export function readFinancialAssumptions(
  siteId: string,
  raw: FinancialAssumptions | null | undefined,
): FinancialAssumptions | null {
  if (!raw) return null;
  if (raw.businessModelKind === 'data-center-capacity-fee') {
    return writeFinancialAssumptions(siteId, raw);
  }
  const migrated = migrateFinancialAssumptionsRecord(raw);
  return { ...migrated, siteId };
}

/**
 * Write path — stamps siteId, bumps to v2, syncs campus rollup from phases.
 */
export function writeFinancialAssumptions(
  siteId: string,
  data: FinancialAssumptions,
): FinancialAssumptions {
  if (data.businessModelKind !== undefined && data.businessModelKind !== 'generation-ppa'
    && data.businessModelKind !== 'data-center-capacity-fee') throw new Error('Unsupported financial business-model kind.');
  if (data.businessModelKind === 'data-center-capacity-fee') {
    const model = dataCenterFinancialModelV1Schema.parse(data.dataCenterModel);
    if (model.siteId !== siteId || data.currency !== 'USD') throw new Error('Data-center model site/currency mismatch.');
    if (data.scenarios?.length) throw new Error('Generation/PPA scenario overrides cannot price a data-center model.');
    // The annual ledger owns DC inputs. Never manufacture generation phases,
    // CF, COD, PPA, LCOE or copied source NPV/IRR alongside it.
    const empty = emptyFinancialAssumptions(data.updatedAt, data.updatedBy);
    return {
      ...empty, siteId, businessModelKind: 'data-center-capacity-fee',
      dataCenterModel: model, phases: [], source: data.source, currency: 'USD',
      updatedAt: data.updatedAt, updatedBy: data.updatedBy,
      sourceFileId: data.sourceFileId, sourceFileName: data.sourceFileName,
      sourceFileHash: data.sourceFileHash,
    };
  }
  if (data.dataCenterModel) throw new Error('Data-center inputs require their explicit business-model discriminator.');
  // DEV GUARD — surface a campus-level write the rollup is about to discard.
  // Warn, never throw: dropping the save would be worse than the bug. In tests
  // and dev this makes the mistake loud at the moment it happens; production
  // behaviour is unchanged.
  if (process.env.NODE_ENV !== 'production') {
    const discarded = detectDiscardedCampusWrites(data);
    if (discarded.length > 0) {
      const detail = discarded
        .map((d) => `${d.field}: wrote ${String(d.attempted)}, phases roll up to ${String(d.rolledUp)}`)
        .join('; ');
      // eslint-disable-next-line no-console
      console.warn(
        `[financialAssumptions] campus-level write DISCARDED by the phase rollup (${detail}). `
        + '`phases[] `is authoritative on write — use `applyCampusPatch(fa, patch)`, which writes the '
        + 'phase and reports `unapplied` when a multi-phase model cannot attribute the value.',
      );
    }
  }
  const withPhases = migrateFinancialAssumptionsRecord(data);
  const synced = syncCampusRollupFromPhases(withPhases);
  return {
    ...synced,
    siteId,
    // Stamp the phase-authoritative generation: everything written through this
    // path is taken literally on the way back in.
    modelVersion: FINANCIAL_MODEL_VERSION_CURRENT,
    updatedAt: data.updatedAt,
    updatedBy: data.updatedBy,
  };
}

export function emptyFinancialAssumptions(now: string, updatedBy: string): FinancialAssumptions {
  const base: FinancialAssumptionsCore = {
    technology: 'tbd',
    capacityMW: null,
    capacityFactorPct: null,
    projectLifeYears: null,
    inServiceDate: null,
    capexTotalUsd: null,
    capexPerKwUsd: null,
    constructionMonths: null,
    decommissioningUsd: null,
    workingCapitalUsd: null,
    contingencyPct: null,
    fixedOmPerKwYearUsd: null,
    variableOmPerMwhUsd: null,
    fuelCostPerMwhUsd: null,
    insuranceAndPropertyTaxPct: null,
    debtToEquityRatio: null,
    debtRatePct: null,
    equityHurdlePct: null,
    waccPct: null,
    taxRatePct: null,
    itcPct: null,
    ptcPerMwhUsd: null,
    depreciationMethod: null,
    ppaPricePerMwhUsd: null,
    ppaEscalatorPctPerYear: null,
    ppaTermYears: null,
    lcoeNominalPerMwhUsd: null,
    lcoeRealPerMwhUsd: null,
    npvUsd: null,
    projectIrrPct: null,
    equityIrrPct: null,
    paybackYears: null,
  };
  const phase = financialPhaseFromCore(base);
  return writeFinancialAssumptions('new', {
    ...base,
    source: 'manual-entry',
    currency: 'USD',
    updatedAt: now,
    updatedBy,
    fieldProvenance: {},
    phases: [phase],
    modelVersion: FINANCIAL_MODEL_VERSION_CURRENT,
  });
}

/**
 * Pure resolver — site-level financial assumptions with an optional
 * per-report override on top. Mirrors `resolveSiteConfig` from SC2-4.
 *
 * Returns `null` when neither layer carries a value (UI shows "Not
 * set yet"; the LLM block emits nothing).
 */
export function resolveFinancialAssumptions(
  siteAssumptions: FinancialAssumptions | undefined,
  reportOverride?: FinancialAssumptions,
): FinancialAssumptions | null {
  if (reportOverride) return reportOverride;
  if (siteAssumptions) return siteAssumptions;
  return null;
}
// FA13 — PPA term-sheet types
// ─────────────────────────────────────────────────────────────────────────────

export type PpaSettlementType = 'physical' | 'financial' | 'hybrid';
export type PpaProductType = 'fixed' | 'indexed' | 'hub-settled' | 'mixed';

/**
 * Structured PPA term-sheet shape. The revenue-facing fields
 * (`ppaPricePerMwhUsd`, `ppaEscalatorPctPerYear`, `ppaTermYears`) are mirrored
 * back into `FinancialAssumptions` so downstream LCOE/IRR calcs see them; the
 * full sheet is persisted alongside for the LLM prompt + risk section.
 */
export interface PpaTermSheet {
  ppaPricePerMwhUsd: number | null;
  ppaEscalatorPctPerYear: number | null;
  ppaTermYears: number | null;
  curtailmentCapPct: number | null;
  curtailmentMakeWholeRate: number | null; // $/MWh
  merchantTailYears: number | null;
  counterparty: string | null;
  creditRating: string | null;
  deliveryPoint: string | null;
  settlementType: PpaSettlementType | null;
  productType: PpaProductType | null;
}

export interface PpaExtractionSummary {
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  missing: number;
}

export interface PpaExtractionResult {
  terms: Partial<PpaTermSheet>;
  fieldProvenance: Partial<Record<keyof PpaTermSheet, FieldProvenance>>;
  warnings: string[];
  summary: PpaExtractionSummary;
}

// ─────────────────────────────────────────────────────────────────────────────
// FA14 — IRA tax-credit types
// ─────────────────────────────────────────────────────────────────────────────

export type TechnologyKey =
  | 'smr'
  | 'nuclear-existing'
  | 'solar-bess'
  | 'wind-bess'
  | 'storage'
  | 'gas-cc'
  | 'gas-sc';

export type OwnershipStructure =
  | 'sponsor-equity'
  | 'tax-equity-partnership'
  | 'direct-pay'
  | 'transfer';

export interface TaxCreditInputs {
  technology: TechnologyKey;
  inServiceDate: string; // ISO; gates pre/post-IRA and §45Y/§48E start
  capacityMW: number;
  domesticContentPct: number | null; // % domestic content; ≥40% (or threshold) trips bonus
  energyCommunityFlag: boolean;
  /**
   * PW/A COMPLIANCE IS TRI-STATE, and `null` is the honest default.
   *
   * These two gate the §45Y/§48E 5x multiplier — the whole distance between a
   * 6% and a 30% ITC. `false` is not a neutral placeholder for "we don't
   * know": it ASSERTS non-compliance and yields a specific rate that then
   * prints as evidence. A caller that has not asked the user must pass `null`,
   * which withholds the rate instead of inventing one.
   */
  prevailingWageCompliant: boolean | null;
  apprenticeshipCompliant: boolean | null;
  lowIncomeCommunity: boolean;
  ownershipStructure: OwnershipStructure;
}

export interface IraBonusDelta {
  name: string;
  deltaPct?: number; // ITC pts
  deltaPerMwh?: number; // PTC $/MWh delta
  condition: string;
}

/**
 * Output of `calculateTaxCredits`. Per-year credit values + total NPV
 * combine for downstream LCOE/NPV updates. `eligibilityNotes` is the
 * human-readable bullet list rendered in the form's disclosure.
 */
export interface IraCreditResult {
  itcPct: number | null; // §48E ITC %
  ptcPerMwhUsd: number | null; // §45Y / §45U PTC year-1 nominal $/MWh
  ptcEscalatorPctPerYear: number;
  ptcDurationYears: number;
  perYearCreditUsd: number[]; // one entry per `ptcDurationYears` (PTC stream)
  itcOneTimeCreditUsd: number; // year-0 ITC cash value (or zero if PTC route)
  totalCreditValueUsd: number; // sum-of-credits, undiscounted
  totalCreditNpvUsd: number; // NPV @ caller-supplied discount or default 8%
  eligibilityNotes: string[];
  baselineRate: number;
  bonusBreakdown: IraBonusDelta[];
  citations: string[]; // IRC §§ list, e.g. ["§45Y", "§48E", "§6417"]
  citedSection: '§45Y' | '§48E' | '§45U' | '§45' | '§48' | 'none';
  directPayEligible: boolean;
  /**
   * True when PW/A compliance was undeclared, so the governing rate could not
   * be resolved. `itcPct` / `ptcPerMwhUsd` are null in that case and the
   * credit values are zero — absence of a quantified credit, NOT a claim that
   * the project earns none.
   */
  rateWithheld: boolean;
  /** Why the rate was withheld, and what to declare to resolve it. */
  withheldReason: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// FA15 — Similar-precedent comparison
// ─────────────────────────────────────────────────────────────────────────────

export type PrecedentMetric =
  | 'lcoeNominalPerMwhUsd'
  | 'capexPerKwUsd'
  | 'fixedOmPerKwYearUsd'
  | 'capacityFactorPct'
  | 'projectIrrPct'
  | 'npvUsd'
  | 'paybackYears';

export interface PrecedentBand {
  metric: PrecedentMetric;
  userValue: number | null;
  cohortSize: number;
  p10: number;
  p50: number;
  p90: number;
  unit: string;
  withinTypicalRange: boolean; // P10 ≤ userValue ≤ P90
  percentile: number | null; // user's value's percentile within the cohort
}

export interface PrecedentComparison {
  cohortSize: number;
  bands: PrecedentBand[];
  cohortDescription: string; // e.g. "SMR · 200-300 MW · USA"
  insufficientData: boolean; // true when cohortSize < 5
}

// ─────────────────────────────────────────────────────────────────────────────
// FA16 — Cross-validation findings
// ─────────────────────────────────────────────────────────────────────────────

export type CrossValidationSeverity = 'info' | 'warning' | 'error';
export type CrossValidationField =
  | 'capacityMW'
  | 'technology'
  | 'capexPerKwUsd'
  | 'capacityFactorPct'
  | 'itcPct'
  | 'ptcPerMwhUsd'
  | 'phases';

/**
 * One-click sync action shape. `targetSurface` tells the UI which store to
 * update; `payload` is the structured patch.
 */
export interface CrossValidationSync {
  targetSurface: 'financialAssumptions' | 'projectIntent';
  field: string;
  newValue: number | string | boolean | null;
  description: string;
}

export interface CrossValidationFinding {
  id: string; // stable string id, e.g. "cv-capacity-mismatch"
  field: CrossValidationField;
  severity: CrossValidationSeverity;
  message: string;
  sync?: CrossValidationSync; // optional one-click resolution
}
